// src/components/configuration/sections/services/ServiceEditor.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Pencil,
  Check,
  X,
  Plus,
  DollarSign,
  Package,
  Settings as SettingsIcon,
  Edit3,
  Trash2,
  Video,
  Save
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Switch } from '../../../ui/switch';
import { Separator } from '../../../ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { cn } from '../../../../utils/cn';
import { useTeams } from '../../../../hooks/useTeams';
import { useZoom } from '../../../../hooks/useZoom';

// ─── Initial form constants (same shape for form + baseline) ───
const initialBaseForm = {
  name: '',
  base_price: '',
  base_duration: '',
  description: '',
  color_code: '#8B5CF6'
};

const initialSettingsForm = {
  active: true,
  requires_confirmation: false,
  is_video_conference: false
};

// ─── Helpers ───
const deriveBaseForm = (service) => ({
  name: service.name || '',
  base_price: service.base_price ?? '',
  base_duration: service.base_duration ?? '',
  description: service.description || '',
  color_code: service.color_code || '#8B5CF6'
});

const deriveSettingsForm = (service) => ({
  active: service.active !== false,
  requires_confirmation: service.requires_confirmation || false,
  is_video_conference: service.is_video_conference || false
});

const ServiceEditor = ({
  service,
  serviceComponents = [],
  onUpdateService,
  onCreateComponent,
  onUpdateComponent,
  onDeleteComponent,
  onCreateService,
  savingBaseService = false,
  savingServiceSettings = false,
  savingComponents = false,
  leftPanelCollapsed,
  onToggleLeftPanel
}) => {
  // ─── Base Service form state ───
  const [baseForm, setBaseForm] = useState(initialBaseForm);
  const [baseBaseline, setBaseBaseline] = useState(initialBaseForm);

  // ─── Service Settings form state ───
  const [settingsForm, setSettingsForm] = useState(initialSettingsForm);
  const [settingsBaseline, setSettingsBaseline] = useState(initialSettingsForm);

  // ─── Name editing visual state ───
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [animateColor, setAnimateColor] = useState(false);

  // ─── Add-on inline CRUD state ───
  const [inlineAddonForm, setInlineAddonForm] = useState({
    name: '', description: '', price: '', duration_minutes: ''
  });
  const [editingInlineId, setEditingInlineId] = useState(null);
  const [editingInlineForm, setEditingInlineForm] = useState({
    name: '', description: '', price: '', duration_minutes: ''
  });

  // ─── Service switch tracking ───
  const prevServiceId = useRef(null);

  // ─── Saving flag watchers (useRef for true→false detection) ───
  const prevSavingBase = useRef(false);
  const prevSavingSettings = useRef(false);

  // Teams & Zoom connection
  const { connected: teamsConnected } = useTeams();
  const { connected: zoomConnected } = useZoom();

  // Color options
  const colorOptions = [
    '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B',
    '#EF4444', '#EC4899', '#8B5A2B', '#6366F1'
  ];

  // ─── Dirty tracking ───
  const hasBaseChanges = Object.keys(initialBaseForm).some(
    key => baseForm[key] !== baseBaseline[key]
  );
  const hasSettingsChanges = Object.keys(initialSettingsForm).some(
    key => settingsForm[key] !== settingsBaseline[key]
  );

  // ─── Initialization: service switch vs server update ───
  useEffect(() => {
    if (!service) return;

    const serviceChanged = prevServiceId.current !== service.id;
    prevServiceId.current = service.id;

    if (serviceChanged) {
      // User selected a different service — always reset both forms
      const newBase = deriveBaseForm(service);
      const newSettings = deriveSettingsForm(service);
      setBaseForm(newBase);
      setBaseBaseline(newBase);
      setSettingsForm(newSettings);
      setSettingsBaseline(newSettings);
      setEditingName(service.name || '');
      setIsEditingName(false);
      setEditingInlineId(null);
    } else {
      // Same service updated from server — only sync if user hasn't made changes
      if (!hasBaseChanges) {
        const newBase = deriveBaseForm(service);
        setBaseForm(newBase);
        setBaseBaseline(newBase);
      }
      if (!hasSettingsChanges) {
        const newSettings = deriveSettingsForm(service);
        setSettingsForm(newSettings);
        setSettingsBaseline(newSettings);
      }
    }
  }, [service]);

  // ─── Saving flag watcher: Base Service ───
  useEffect(() => {
    if (prevSavingBase.current && !savingBaseService && service) {
      const newBase = deriveBaseForm(service);
      setBaseForm(newBase);
      setBaseBaseline(newBase);
      setEditingName(service.name || '');
      setIsEditingName(false);
    }
    prevSavingBase.current = savingBaseService;
  }, [savingBaseService, service]);

  // ─── Saving flag watcher: Service Settings ───
  useEffect(() => {
    if (prevSavingSettings.current && !savingServiceSettings && service) {
      const newSettings = deriveSettingsForm(service);
      setSettingsForm(newSettings);
      setSettingsBaseline(newSettings);
    }
    prevSavingSettings.current = savingServiceSettings;
  }, [savingServiceSettings, service]);

  // ─── Base Service handlers ───
  const handleBaseFieldChange = (field, value) => {
    setBaseForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveBase = () => {
    const changedFields = {};
    Object.keys(baseForm).forEach(key => {
      if (baseForm[key] !== baseBaseline[key]) {
        if (key === 'base_price') {
          changedFields[key] = baseForm[key] ? parseFloat(baseForm[key]) : null;
        } else if (key === 'base_duration') {
          changedFields[key] = baseForm[key] ? parseInt(baseForm[key]) : null;
        } else if (key === 'description') {
          changedFields[key] = baseForm[key]?.trim() || null;
        } else if (key === 'name') {
          changedFields[key] = baseForm[key].trim();
        } else {
          changedFields[key] = baseForm[key];
        }
      }
    });
    if (Object.keys(changedFields).length > 0) {
      onUpdateService(service.id, changedFields, { savingFlag: 'savingBaseService' });
    }
  };

  // ─── Name editing (contentEditable visual, defers to base save button) ───
  const handleStartEditName = () => {
    setIsEditingName(true);
    setEditingName(baseForm.name);
  };

  const handleConfirmName = () => {
    const trimmed = editingName.trim();
    if (trimmed && trimmed !== baseForm.name) {
      setBaseForm(prev => ({ ...prev, name: trimmed }));
    }
    setIsEditingName(false);
  };

  const handleCancelEditName = () => {
    setEditingName(baseForm.name);
    setIsEditingName(false);
  };

  // ─── Color change (updates local state only, saved with base save button) ───
  const handleColorChange = (color) => {
    handleBaseFieldChange('color_code', color);
    setAnimateColor(true);
  };

  // ─── Service Settings handlers ───
  const handleSettingsFieldChange = (field, value) => {
    setSettingsForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = () => {
    const changedFields = {};
    Object.keys(settingsForm).forEach(key => {
      if (settingsForm[key] !== settingsBaseline[key]) {
        changedFields[key] = settingsForm[key];
      }
    });
    if (Object.keys(changedFields).length > 0) {
      onUpdateService(service.id, changedFields, { savingFlag: 'savingServiceSettings' });
    }
  };

  // ─── Add-on CRUD handlers ───
  const handleCreateInlineAddon = () => {
    if (!inlineAddonForm.name.trim() || !inlineAddonForm.description.trim() ||
        !inlineAddonForm.price || !inlineAddonForm.duration_minutes) return;

    onCreateComponent(service.id, {
      name: inlineAddonForm.name.trim(),
      description: inlineAddonForm.description.trim(),
      price: parseFloat(inlineAddonForm.price),
      duration_minutes: parseInt(inlineAddonForm.duration_minutes),
      component_type: 'addon'
    });

    setInlineAddonForm({ name: '', description: '', price: '', duration_minutes: '' });
  };

  const handleStartInlineEdit = (component) => {
    setEditingInlineId(component.id);
    setEditingInlineForm({
      name: component.name || '',
      description: component.description || '',
      price: component.price || '',
      duration_minutes: component.duration_minutes || ''
    });
  };

  const handleSaveInlineEdit = () => {
    if (!editingInlineForm.name.trim() || !editingInlineForm.description.trim() ||
        !editingInlineForm.price || !editingInlineForm.duration_minutes) return;

    onUpdateComponent(service.id, editingInlineId, {
      name: editingInlineForm.name.trim(),
      description: editingInlineForm.description.trim(),
      price: parseFloat(editingInlineForm.price),
      duration_minutes: parseInt(editingInlineForm.duration_minutes),
      component_type: 'addon'
    });
    setEditingInlineId(null);
  };

  const handleCancelInlineEdit = () => {
    setEditingInlineId(null);
    setEditingInlineForm({ name: '', description: '', price: '', duration_minutes: '' });
  };

  const handleDeleteComponent = (component) => {
    if (confirm(`Delete "${component.name}"?`)) {
      onDeleteComponent(service.id, component.id, component.name);
    }
  };

  // ─── Empty state ───
  if (!service) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/20">
        <div className="text-center p-8">
          <SettingsIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Select a Service</h3>
          <p className="text-muted-foreground text-sm mb-6">
            Choose a service from the list to view and edit its details
          </p>
          {onCreateService && (
            <Button onClick={onCreateService} className="flex items-center gap-2 mx-auto">
              <Plus className="w-4 h-4" />
              Create Your First Service
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
      <div className="flex-1 overflow-y-auto pt-20 pl-36 pr-36 pb-20 space-y-10">
        {/* ─── Header: Name + Color ─── */}
        <div className="space-y-4">
          <div className="flex items-start gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 group">
                <h1
                  ref={(el) => {
                    if (el && isEditingName) {
                      el.focus();
                      const range = document.createRange();
                      const sel = window.getSelection();
                      range.selectNodeContents(el);
                      range.collapse(false);
                      sel.removeAllRanges();
                      sel.addRange(range);
                    }
                  }}
                  contentEditable={isEditingName}
                  suppressContentEditableWarning
                  onInput={(e) => setEditingName(e.currentTarget.textContent)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleConfirmName(); }
                    if (e.key === 'Escape') { e.preventDefault(); handleCancelEditName(); }
                  }}
                  onClick={isEditingName ? undefined : handleStartEditName}
                  className={cn(
                    "text-4xl font-bold text-foreground flex-1 outline-none",
                    isEditingName ? "cursor-text" : "truncate cursor-pointer"
                  )}
                >
                  {baseForm.name}
                </h1>

                {isEditingName ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={handleConfirmName}
                      className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10">
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelEditName}
                      className="h-8 w-8 p-0">
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" onClick={handleStartEditName}
                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                )}
              </div>
            </div>

            {!isEditingName && (
              <div className="flex items-center gap-2 shrink-0">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorChange(color)}
                    className={cn(
                      "w-7 h-7 rounded-lg border-2 transition-all duration-200",
                      "hover:scale-110 focus:outline-none",
                      baseForm.color_code === color
                        ? "scale-110 border-foreground shadow-lg"
                        : "border-border hover:border-muted-foreground"
                    )}
                    style={{
                      backgroundColor: color,
                      boxShadow: baseForm.color_code === color ? `0 0 8px ${color}60` : 'none'
                    }}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="h-0.5 w-full overflow-hidden">
            <div
              className={cn("h-full w-full transition-all", animateColor && "animate-color-sweep")}
              style={{ background: `linear-gradient(to right, ${baseForm.color_code} 0%, ${baseForm.color_code}00 100%)` }}
              onAnimationEnd={() => setAnimateColor(false)}
            />
          </div>

          <style jsx>{`
            @keyframes color-sweep {
              0% { transform: translateX(-100%); opacity: 0; }
              1% { opacity: 1; }
              100% { transform: translateX(0); opacity: 1; }
            }
            .animate-color-sweep {
              animation: color-sweep 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            }
          `}</style>
        </div>

        {/* ─── Base Service Section ─── */}
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                <DollarSign className="w-5 h-5" />
                Base Service
              </h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Set the starting price and duration for this service. This forms the foundation of your service offering and is always included when a customer books. You can add optional extras below.
              </p>
            </div>
            <Button
              onClick={handleSaveBase}
              disabled={!hasBaseChanges || savingBaseService}
              size="sm"
              className="shrink-0 bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 shadow-[0_2px_4px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_6px_16px_rgba(255,255,255,0.15),0_4px_8px_rgba(0,0,0,0.5)] hover:border-zinc-500 hover:-translate-y-1 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {savingBaseService ? 'Saving...' : 'Save'}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="base_price" className="text-sm font-medium">Base Price</Label>
              <Input
                id="base_price"
                type="number"
                step="0.01"
                min="0"
                value={baseForm.base_price}
                onChange={(e) => handleBaseFieldChange('base_price', e.target.value)}
                placeholder="0.00"
                className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="base_duration" className="text-sm font-medium">Duration (minutes)</Label>
              <Input
                id="base_duration"
                type="number"
                min="1"
                value={baseForm.base_duration}
                onChange={(e) => handleBaseFieldChange('base_duration', e.target.value)}
                placeholder="60"
                className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">What's Included (optional)</Label>
            <Textarea
              id="description"
              value={baseForm.description}
              onChange={(e) => handleBaseFieldChange('description', e.target.value)}
              placeholder="Describe what's included in the base service..."
              rows={2}
              className="resize-none bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
            />
          </div>
        </div>

        <Separator />

        {/* ─── Optional Add-ons Section (CRUD pattern) ─── */}
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                <Package className="w-5 h-5" />
                Optional Add-ons
                {serviceComponents.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full font-normal">
                    {serviceComponents.length}
                  </span>
                )}
              </h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Create customizable extras that customers can add to their service booking. Each add-on can have its own price and duration, giving customers flexibility while increasing your revenue opportunities.
              </p>
            </div>
          </div>

          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">Price</TableHead>
                  <TableHead className="w-24">Duration</TableHead>
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serviceComponents.map((component) => {
                  const isEditing = editingInlineId === component.id;

                  if (isEditing) {
                    return (
                      <TableRow key={component.id}>
                        <TableCell>
                          <Input
                            value={editingInlineForm.name}
                            onChange={(e) => setEditingInlineForm(prev => ({ ...prev, name: e.target.value }))}
                            className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingInlineForm.description}
                            onChange={(e) => setEditingInlineForm(prev => ({ ...prev, description: e.target.value }))}
                            className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" step="0.01" min="0"
                            value={editingInlineForm.price}
                            onChange={(e) => setEditingInlineForm(prev => ({ ...prev, price: e.target.value }))}
                            className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" min="0"
                            value={editingInlineForm.duration_minutes}
                            onChange={(e) => setEditingInlineForm(prev => ({ ...prev, duration_minutes: e.target.value }))}
                            className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={handleSaveInlineEdit}
                              disabled={!editingInlineForm.name.trim() || !editingInlineForm.description.trim() || !editingInlineForm.price || !editingInlineForm.duration_minutes || savingComponents}
                              className="h-7 w-7 p-0 hover:bg-emerald-500/10 text-emerald-500">
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={handleCancelInlineEdit}
                              className="h-7 w-7 p-0 hover:bg-zinc-700/50">
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={component.id} className="group">
                      <TableCell className="font-medium">{component.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {component.description || '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        ${component.price ? parseFloat(component.price).toFixed(2) : '0.00'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {component.duration_minutes || 0} min
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" onClick={() => handleStartInlineEdit(component)}
                            className="h-7 w-7 p-0 hover:bg-zinc-700/50">
                            <Edit3 className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteComponent(component)}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Inline Add Row */}
                <TableRow className="border-t-2 border-border/50">
                  <TableCell>
                    <Input
                      value={inlineAddonForm.name}
                      onChange={(e) => setInlineAddonForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Add-on name..."
                      className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={inlineAddonForm.description}
                      onChange={(e) => setInlineAddonForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description..."
                      className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number" step="0.01" min="0"
                      value={inlineAddonForm.price}
                      onChange={(e) => setInlineAddonForm(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0.00"
                      className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number" min="0"
                      value={inlineAddonForm.duration_minutes}
                      onChange={(e) => setInlineAddonForm(prev => ({ ...prev, duration_minutes: e.target.value }))}
                      placeholder="0"
                      className="h-8 text-sm bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      onClick={handleCreateInlineAddon}
                      disabled={!inlineAddonForm.name.trim() || !inlineAddonForm.description.trim() || !inlineAddonForm.price || !inlineAddonForm.duration_minutes || savingComponents}
                      size="sm"
                      className="h-8 px-3 bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 shadow-[0_2px_4px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_6px_16px_rgba(255,255,255,0.15),0_4px_8px_rgba(0,0,0,0.5)] hover:border-zinc-500 hover:-translate-y-1 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      Add
                    </Button>
                  </TableCell>
                </TableRow>

                {serviceComponents.length === 0 && !inlineAddonForm.name && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                      No add-ons yet. Fill in the row above to create your first add-on.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <Separator />

        {/* ─── Service Settings Section ─── */}
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                <SettingsIcon className="w-5 h-5" />
                Service Settings
              </h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Control how this service appears to customers and whether bookings require manual approval. Active services are immediately bookable, while requiring confirmation gives you control over each appointment.
              </p>
            </div>
            <Button
              onClick={handleSaveSettings}
              disabled={!hasSettingsChanges || savingServiceSettings}
              size="sm"
              className="shrink-0 bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700 shadow-[0_2px_4px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_6px_16px_rgba(255,255,255,0.15),0_4px_8px_rgba(0,0,0,0.5)] hover:border-zinc-500 hover:-translate-y-1 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {savingServiceSettings ? 'Saving...' : 'Save'}
            </Button>
          </div>

          <div className="space-y-3">
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
              <div>
                <Label className="font-medium text-sm">Service Active</Label>
                <p className="text-xs text-muted-foreground mt-1">Make this service available for booking</p>
              </div>
              <Switch
                checked={settingsForm.active}
                onCheckedChange={(checked) => handleSettingsFieldChange('active', checked)}
              />
            </div>

            {/* Requires Confirmation Toggle */}
            <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
              <div>
                <Label className="font-medium text-sm">Requires Confirmation</Label>
                <p className="text-xs text-muted-foreground mt-1">Appointments need manual approval</p>
              </div>
              <Switch
                checked={settingsForm.requires_confirmation}
                onCheckedChange={(checked) => handleSettingsFieldChange('requires_confirmation', checked)}
              />
            </div>

            {/* Virtual Meeting Service Toggle */}
            <div className="flex items-center justify-between p-4 border border-zinc-700/50 rounded-lg bg-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
              <div className="flex-1">
                <Label className="font-medium text-sm flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Virtual Meeting Service
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {teamsConnected || zoomConnected
                    ? `Automatically create ${
                        teamsConnected && zoomConnected
                          ? 'Teams or Zoom'
                          : teamsConnected
                          ? 'Teams'
                          : 'Zoom'
                      } meetings for appointments`
                    : 'Connect Microsoft Teams or Zoom in Integrations to enable virtual meetings'}
                </p>
              </div>
              <Switch
                checked={settingsForm.is_video_conference}
                disabled={!teamsConnected && !zoomConnected}
                onCheckedChange={(checked) => handleSettingsFieldChange('is_video_conference', checked)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceEditor;
